/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import React, { useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import { color, space, typography } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { debounce } from 'lodash';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useSelector } from 'react-redux';
import selectAuth from 'redux-api/reselect/auth';
import { getAnalyticsFilters } from 'api';
import Text from 'theme/text';
import { StyledSelect } from 'theme/select';
import InputError from 'theme/input-error';
import translations from 'translations';

const { Option } = Select;

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  
  &:hover {
    background-color: ${themeGet('colors.primary_50')};
  }
`;

const ExcludeButton = styled.span`
  color: ${themeGet('colors.red_400')};
  font-weight: bold;
  font-size: 9px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  cursor: pointer;
  user-select: none;
  background-color: ${themeGet('colors.red_50')};
  border: 1px solid ${themeGet('colors.red_200')};
  line-height: 1;
  
  &:hover {
    background-color: ${themeGet('colors.red_100')};
    border-color: ${themeGet('colors.red_300')};
  }
`;

const TitleCard = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;

  & > .cover {
    width: 48px;
  }

  & > .right {
    width: 180px;
    margin-left: 8px;
  }

  & > .without-cover {
    width: 100%;
  }
`;

const TextEllipsis = styled.p`
  ${typography}
  ${color}
  ${space}

  text-overflow:ellipsis;
  overflow: hidden;
  display: -webkit-box !important;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

function SelectRemote({
  className,
  defaultValue,
  disabled,
  input,
  meta,
  style,
  noError,
  languageCode,
  data,
  dataType,
}) {
  const {
    signInUserSession: { accessToken },
    adminRequestedStreetlibInternalId,
  } = useSelector(selectAuth);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const query = useInfiniteQuery(
    ['filter', data.type, searchQuery],
    ({ pageParam = 1 }) => {
      const params = {
        [data.params]: searchQuery,
        page: pageParam,
        locale: data.locale ? languageCode : undefined,
      };

      if (dataType === 'usage_data') params['q[anay_filter_usage_eq]'] = true;
      else if (dataType === 'sales_data') params['q[anay_filter_sales_eq]'] = true;
      else params['q[anay_filter_earns_eq]'] = true;

      return getAnalyticsFilters(
        languageCode,
        accessToken,
        adminRequestedStreetlibInternalId,
        data.endpoint,
        params,
      );
    },
    {
      enabled: isOpen,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.headers['per-page'] * allPages.length <= lastPage.headers.total
          ? allPages.length + 1
          : undefined,
    },
  );

  const handleChange = useCallback(
    (value, option) => {
      if (value === '') {
        return null;
      }

      if (value === 'moreKey') {
        return query.fetchNextPage();
      }

      setIsOpen(false);
      setSearchInput('');
      setSearchQuery('');

      let label = '';

      if (data.type === 'title_search' || data.type === 'main_category_search') {
        // For title_search, we need to find the original option to get the ISBN
        // For main_category_search, we need the name_localized
        const originalOption = query.data?.pages?.flatMap(page => page.data)?.find(opt => {
          const optValue = `${data.valueKey}=${opt[data.value]}`;
          return optValue === value;
        });
        
        if (originalOption) {
          if (data.type === 'title_search') {
            // Use ISBN for title_search instead of title
            label = originalOption.isbn || originalOption.title || value.split('=')[1] || value;
          } else {
            // For main_category_search, use name_localized
            label = originalOption.name_localized || originalOption.name || value.split('=')[1] || value;
          }
        } else {
          // Fallback: try to extract from option structure
          try {
            const {
              children: {
                props: { children },
              },
            } = option;
            const child = data.type === 'main_category_search' ? children : children[1];
            const {
              props: { children: rightChildren },
            } = child;

            if (data.type === 'title_search') {
              // Try to get ISBN (second child) instead of title (first child)
              const isbn = rightChildren[1]?.props?.children || rightChildren[0]?.props?.children;
              label = isbn;
            } else {
              const title = rightChildren[0].props.children;
              label = title;
            }
          } catch (error) {
            // Fallback: use the value if we can't extract the label
            label = value.split('=')[1] || value;
          }
        }
      } else if (option.children && typeof option.children === 'object') {
        // Real data is in option.children, need to extract it
        // Try to extract data from children props
        if (option.children.props && option.children.props.children) {
          // If children is an array, take the first element (the name text)
          // The second element is the NOT button
          if (Array.isArray(option.children.props.children)) {
            const firstChild = option.children.props.children[0];
            
            // If the first child is a React object, extract text from its props
            if (firstChild && typeof firstChild === 'object' && firstChild.props) {
              if (firstChild.props.children) {
                label = String(firstChild.props.children);
              } else {
                label = String(firstChild);
              }
            } else {
              label = String(firstChild);
            }
          } else {
            label = String(option.children.props.children);
          }
        } else {
          // If nothing found in props, try to find the original option data
          // Need to find the original option data that was used to create the Option
          // Try to extract from value which should contain the data
          // The value is "store_id=44", need to find the original option
          // Search in the options array to find the one with the same value
          const originalOption = query.data?.pages?.flatMap(page => page.data)?.find(opt => {
            const optValue = `${data.valueKey}=${opt[data.value]}`;
            return optValue === value;
          });
          
          if (originalOption) {
            if (data.labelKey === 'name_localized' && !originalOption[data.labelKey]) {
              label = originalOption.name;
            } else if (data.labelKey2 && originalOption[data.labelKey2]) {
              label = `${originalOption[data.labelKey]} (${originalOption[data.labelKey2]})`;
            } else {
              label = originalOption[data.labelKey];
            }
          } else {
            // Fallback: use the value
            label = value.split('=')[1] || value;
          }
        }
      } else if (option[data.labelKey]) {
        // If nothing found, try with option directly
        if (data.labelKey === 'name_localized' && !option[data.labelKey]) {
          label = option.name;
        } else if (data.labelKey2 && option[data.labelKey2]) {
          label = `${option[data.labelKey]} (${option[data.labelKey2]})`;
        } else {
          label = option[data.labelKey];
        }
      } else if (option.props && option.props.children) {
        // If still nothing found, try with option data
        // Try to extract from option props
        label = String(option.props.children);
      } else {
        // Fallback: use the value
        label = value.split('=')[1] || value;
      }

      // Ensure label is a primitive string and not empty
      let cleanLabel = String(label || '');
      if (!cleanLabel || cleanLabel === 'undefined' || cleanLabel === 'null') {
        cleanLabel = value.split('=')[1] || value;
      }

      return input.onChange({
        type: data.type,
        value,
        label: cleanLabel,
        color: data.color,
      }, false);
    },
    [input, query, data],
  );

  const handleExcludeClick = useCallback((e, optionValue, optionLabel) => {
    e.stopPropagation();
    
    setIsOpen(false);
    setSearchInput('');
    setSearchQuery('');

    // Ensure optionLabel is a primitive string
    let cleanLabel = '';
    
    if (typeof optionLabel === 'string' && optionLabel.trim()) {
      cleanLabel = optionLabel;
    } else if (typeof optionLabel === 'object' && optionLabel !== null) {
      // If it's a React object, try to extract the text
      try {
        if (optionLabel.props && optionLabel.props.children) {
          cleanLabel = String(optionLabel.props.children);
        } else {
          cleanLabel = String(optionLabel);
        }
      } catch (error) {
        // Fallback: use the value if we can't extract the label
        cleanLabel = optionValue.split('=')[1] || optionValue;
      }
    } else {
      cleanLabel = String(optionLabel || '');
    }

    // Ensure cleanLabel is not empty
    if (!cleanLabel || cleanLabel === 'undefined' || cleanLabel === 'null') {
      cleanLabel = optionValue.split('=')[1] || optionValue;
    }

    return input.onChange({
      type: data.type,
      value: optionValue,
      label: cleanLabel,
      color: data.color,
    }, true); // Always exclusive when clicking the button
  }, [input, data]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const delayedHandleChange = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 1000),
    [],
  );

  const handleSearch = useCallback(
    (e) => {
      if (e.length > 0) {
        setIsOpen(true);
      }
      setSearchInput(e);
      delayedHandleChange(e);
    },
    [delayedHandleChange],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setSearchQuery('');
  }, []);

  const selectOpen = useMemo(
    () => isOpen && (!query.isLoading || searchInput.length > 0),
    [searchInput, isOpen, query],
  );

  return (
    <StyledSelect
      className={className}
      fontSize="display"
      lineHeight="display"
      fontWeight="normal"
      error={meta.error && meta.touched}
      disabled={disabled}
      {...style}
    >
      <Select
        value=""
        onChange={handleChange}
        defaultValue={defaultValue}
        loading={query.isLoading && isOpen}
        open={selectOpen}
        onFocus={handleOpen}
        onBlur={handleClose}
        showSearch={data.showSearch}
        searchValue={searchInput}
        onSearch={data.showSearch ? handleSearch : null}
        filterOption={false}
      >
        {!!query.data &&
          query.data.pages.map((page, pageIndex) => {
            // Create unique key using filter type and search query to avoid index-based keys
            const uniqueKey = `${data.type}-${searchQuery || 'default'}-${pageIndex}-${page.data.length}`;
            return (
            <React.Fragment key={uniqueKey}>
              {page.data.map((option) =>
                data.type === 'title_search' ? (
                  <Option
                    key={`${data.valueKey}=${option[data.value]}`}
                    value={`${data.valueKey}=${option[data.value]}`}
                    className="ant-select-title-option"
                  >
                    <OptionWrapper>
                      <TitleCard>
                        <div className="cover">
                          <img
                            width="100%"
                            height="100%"
                            src={option.cover ? option.cover.url : '/images/book-placeholder.jpg'}
                            alt=""
                          />
                        </div>
                        <div className="right">
                          <TextEllipsis fontSize="pSmall" lineHeight="pSmall" fontWeight="bold">
                            {option.title}
                          </TextEllipsis>
                          <Text fontSize="pSmall" lineHeight="pSmall" fontWeight="medium">
                            {option.isbn}
                          </Text>
                          <Text fontSize="pSmall" lineHeight="pSmall" fontWeight="medium">
                            {option.authors ? option.authors[0].composite_name : ''}
                          </Text>
                        </div>
                      </TitleCard>
                      <ExcludeButton 
                        onClick={(e) => handleExcludeClick(e, `${data.valueKey}=${option[data.value]}`, option.isbn || option.title)}
                      >
                        NOT
                      </ExcludeButton>
                    </OptionWrapper>
                  </Option>
                ) : data.type === 'main_category_search' ? (
                  <Option
                    key={`${data.valueKey}=${option[data.value]}`}
                    value={`${data.valueKey}=${option[data.value]}`}
                    className="ant-select-main_category-option"
                  >
                    <OptionWrapper>
                      <TitleCard>
                        <div className="without-cover">
                          <TextEllipsis fontSize="pSmall" lineHeight="pSmall" fontWeight="bold">
                            {option.name_localized}
                          </TextEllipsis>
                          <Text fontSize="pSmall" lineHeight="pSmall" fontWeight="medium">
                            {option.code}
                          </Text>
                        </div>
                      </TitleCard>
                      <ExcludeButton 
                        onClick={(e) => handleExcludeClick(e, `${data.valueKey}=${option[data.value]}`, option.name_localized)}
                      >
                        NOT
                      </ExcludeButton>
                    </OptionWrapper>
                  </Option>
                ) : (
                  <Option
                    key={`${data.valueKey.replace('[lang]', languageCode)}=${option[data.value]}`}
                    value={`${data.valueKey.replace('[lang]', languageCode)}=${option[data.value]}`}
                  >
                    <OptionWrapper>
                      <span>
                        {data.labelKey === 'name_localized' && !option[data.labelKey]
                          ? option.name
                          : data.labelKey2 && option[data.labelKey2]
                          ? `${option[data.labelKey]} (${option[data.labelKey2]})`
                          : option[data.labelKey]}
                      </span>
                      <ExcludeButton 
                        onClick={(e) => handleExcludeClick(e, `${data.valueKey.replace('[lang]', languageCode)}=${option[data.value]}`, 
                          data.labelKey === 'name_localized' && !option[data.labelKey]
                            ? option.name
                            : data.labelKey2 && option[data.labelKey2]
                            ? `${option[data.labelKey]} (${option[data.labelKey2]})`
                            : option[data.labelKey]
                        )}
                      >
                        NOT
                      </ExcludeButton>
                    </OptionWrapper>
                  </Option>
                ),
              )}
            </React.Fragment>
            );
          })}

        {query.hasNextPage && (
          <Option value="moreKey">{translations[languageCode]['More...']}</Option>
        )}

        {query.isLoading && (!query.data || query.data.pages.length === 0) && <Option value="" />}
      </Select>

      {!noError && (
        <InputError value={input.value} meta={meta} languageCode={languageCode || 'en'} />
      )}
    </StyledSelect>
  );
}

export default SelectRemote;
